<template>
  <div>
    <!-- Header -->
    <b-card class="mb-1">
      <template #header>
        <b-row class="flex-row justify-content-between align-items-center" style="width:100%">
          <b-col>
            <h4 class="mb-0">
              Onboarding — {{ account ? account.AccountName : "" }}
              <small v-if="account" class="text-muted ml-50">{{ account.AccountNumber }}</small>
              <b-badge v-if="onboarding" :variant="statusVariant(onboarding.Status)" class="ml-1">
                {{ onboarding.Status }}
              </b-badge>
            </h4>
            <div v-if="account" class="mt-50">
              <b-badge v-for="chip in accountChips" :key="chip.label" variant="light-secondary" class="mr-50">
                <span class="text-muted">{{ chip.label }}:</span> {{ chip.value }}
              </b-badge>
            </div>
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="$router.push({ name: 'onboarding' })"
            >
              <feather-icon icon="ArrowLeftIcon" size="14" style="margin-bottom:0.3rem" />
              <span class="text-nowrap">Back to List</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <div v-if="loading" class="text-center my-2">
        <b-spinner class="align-middle mr-1" />
        <strong>Loading…</strong>
      </div>

      <b-alert v-else-if="!onboarding" show variant="danger">
        <feather-icon icon="AlertTriangleIcon" class="mr-50" />
        The onboarding could not be loaded.
        <b-link @click="loadAll">Try again</b-link>
      </b-alert>

      <b-tabs v-else content-class="mt-1">
        <!-- ============================== SUMMARY TAB ============================== -->
        <b-tab title="Summary" active>
          <onboarding-summary-tab
            :onboarding-id="onboarding.id"
            :summary="summary"
            :requirements="requirements"
            :form-sections="formSections"
            :section-catalogue="sectionCatalogue"
            @refresh="refresh"
          />
        </b-tab>

        <!-- ============================== FORM TAB ============================== -->
        <b-tab title="Form">
          <div class="d-flex justify-content-end mb-1">
            <b-button variant="outline-primary" size="sm" @click="openFormPreview">
              <feather-icon icon="EyeIcon" size="14" class="mr-25" />
              Preview client form
            </b-button>
          </div>
          <onboarding-form-tab
            :onboarding="onboarding"
            :form-data="formData"
            :form-schema="formSchema"
            :form-sections="formSections"
            :section-catalogue="sectionCatalogue"
            :person-types="personTypes"
            :summary="summary"
            :signature-images="signatureImages"
          />
        </b-tab>

        <!-- ============================== PACKAGES TAB ============================== -->
        <b-tab>
          <template #title>
            Packages
            <b-badge v-if="pendingPackageChanges.length" variant="warning" class="ml-25">{{ pendingPackageChanges.length }}</b-badge>
          </template>
          <onboarding-packages-tab
            :onboarding-id="onboarding.id"
            :applied-packages="appliedPackages"
            :available-packages="availablePackages"
            :pending-package-changes="pendingPackageChanges"
            :section-catalogue="sectionCatalogue"
            @refresh="refresh"
          />
        </b-tab>

        <!-- ============================== REQUIREMENTS TAB ============================== -->
        <b-tab>
          <template #title>
            Requirements
            <b-badge v-if="summary.submitted" variant="info" class="ml-25" title="Waiting for review">{{ summary.submitted }}</b-badge>
          </template>
          <onboarding-requirements-tab
            :onboarding-id="onboarding.id"
            :requirements="requirements"
            :persons="persons"
            :person-types="personTypes"
            :document-types="documentTypes"
            @refresh="refresh"
          />
        </b-tab>

        <!-- ============================== DOCUMENTS TAB ============================== -->
        <b-tab>
          <template #title>
            Documents
            <b-badge v-if="summary.openRequests" variant="warning" class="ml-25" title="Open requests from the client">{{ summary.openRequests }}</b-badge>
          </template>
          <onboarding-documents-tab
            :onboarding-id="onboarding.id"
            :documents="documents"
            :customer-requests="customerRequests"
            :requirements="requirements"
            @refresh="refresh"
          />
        </b-tab>

        <!-- ============================== CONTACTS TAB ============================== -->
        <b-tab title="Contacts">
          <validation-observer ref="contactValidation">
            <b-row class="mb-50">
              <b-col cols="12" md="6">
                <b-form-group label="Select an existing client user" class="mb-0">
                  <v-select
                    v-model="selectedClientUser"
                    :options="clientUsers"
                    label="fullname"
                    :get-option-label="(u) => u.fullname + ' (' + u.email + ')'"
                    placeholder="Search client users…"
                    :clearable="true"
                  />
                  <small class="text-muted">or enter a name and email manually below</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-1 align-items-end">
              <b-col cols="12" md="3">
                <b-form-group label="Name" class="mb-0">
                  <validation-provider #default="{ errors }" :rules="selectedClientUser ? '' : 'required'" name="Contact Name">
                    <b-form-input
                      v-model="newContact.name"
                      :disabled="!!selectedClientUser"
                      :placeholder="selectedClientUser ? selectedClientUser.fullname : ''"
                      :state="errors.length ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Email" class="mb-0">
                  <validation-provider #default="{ errors }" :rules="selectedClientUser ? '' : 'required|email'" name="Contact Email">
                    <b-form-input
                      v-model="newContact.email"
                      :disabled="!!selectedClientUser"
                      :placeholder="selectedClientUser ? selectedClientUser.email : ''"
                      :state="errors.length ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-checkbox v-model="sendInviteEmail" class="mb-50">
                  Send invitation email
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="3">
                <b-button variant="primary" :disabled="contactSaving" @click="createContact">
                  <b-spinner v-if="contactSaving" small class="mr-25" />
                  <feather-icon v-else :icon="sendInviteEmail ? 'SendIcon' : 'UserPlusIcon'" size="14" class="mr-25" />
                  {{ sendInviteEmail ? "Add Contact & Send Link" : "Add Contact" }}
                </b-button>
              </b-col>
            </b-row>
          </validation-observer>

          <b-table
            :items="contacts"
            :fields="contactFields"
            show-empty
            empty-text="No contacts assigned to this account yet."
            responsive
          >
            <template #cell(Name)="{ item }">
              {{ item.Name }}
              <b-badge v-if="item.UserId" variant="light-info" class="ml-25">System user</b-badge>
            </template>
            <template #cell(Status)="{ value }">
              <b-badge :variant="contactStatusVariant(value)">{{ value }}</b-badge>
            </template>
            <template #cell(Link)="{ item }">
              <template v-if="item.Status === 'Active'">
                <code class="d-block text-truncate" style="max-width:280px" :title="contactLink(item)">
                  {{ contactLink(item) }}
                </code>
                <b-link class="small" @click="copyLink(item)">
                  <feather-icon icon="CopyIcon" size="12" class="mr-25" />Copy
                </b-link>
              </template>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(LastAccess)="{ item }">
              <template v-if="item.LastAccessDate">
                {{ item.LastAccessDate }} {{ item.LastAccessTime }}
                <small class="text-muted d-block">IP: {{ item.LastAccessIp }}</small>
              </template>
              <span v-else class="text-muted">Never</span>
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                v-if="item.Status === 'Active'"
                variant="flat-primary"
                size="sm"
                title="Copy link"
                @click="copyLink(item)"
              >
                <feather-icon icon="LinkIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.Status === 'Active'"
                variant="flat-info"
                size="sm"
                title="Resend link email"
                @click="resendLink(item)"
              >
                <feather-icon icon="MailIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.Status === 'Active'"
                variant="flat-warning"
                size="sm"
                title="Freeze access"
                @click="freezeContact(item)"
              >
                <feather-icon icon="PauseCircleIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.Status === 'Frozen'"
                variant="flat-success"
                size="sm"
                title="Re-activate access"
                @click="reactivateContact(item)"
              >
                <feather-icon icon="PlayCircleIcon" size="14" />
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- ============================== ACTIVITY TAB ============================== -->
        <b-tab title="Activity" @click="loadActivities">
          <app-timeline v-if="activities.length">
            <app-timeline-item
              v-for="activity in activities"
              :key="activity.id"
              :title="eventTitle(activity.Event)"
              :time="activity.SaveDate + ' ' + activity.SaveTime"
              :variant="eventVariant(activity.Event)"
              :icon="eventIcon(activity.Event)"
            >
              <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                <h6>{{ eventTitle(activity.Event) }}</h6>
                <small class="timeline-item-time text-nowrap text-muted">{{ activity.SaveDate }} {{ activity.SaveTime }}</small>
              </div>
              <small class="mb-0">{{ activitySubtitle(activity) }}</small>
              <ul v-if="activity.Changes && activity.Changes.length" class="mt-50 mb-0 pl-1" style="list-style:none">
                <li v-for="path in activity.Changes" :key="path" class="text-muted small">
                  <feather-icon icon="EditIcon" size="11" class="mr-25" />
                  {{ formatChangePath(path) }}
                </li>
              </ul>
            </app-timeline-item>
          </app-timeline>
          <b-alert v-else show variant="info">
            <feather-icon icon="InfoIcon" class="mr-50" />
            No activity recorded yet.
          </b-alert>
        </b-tab>
      </b-tabs>
    </b-card>

    <form-preview-modal ref="formPreview" />
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required, email } from "@validations";
import vSelect from "vue-select";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem.vue";
import store from "@/store";
import { onboardingLink } from "@/config/hosts";
import onboardingStoreModule from "./onboardingStoreModule";
import OnboardingSummaryTab from "./components/OnboardingSummaryTab.vue";
import OnboardingFormTab from "./components/OnboardingFormTab.vue";
import OnboardingPackagesTab from "./components/OnboardingPackagesTab.vue";
import OnboardingRequirementsTab from "./components/OnboardingRequirementsTab.vue";
import OnboardingDocumentsTab from "./components/OnboardingDocumentsTab.vue";

const STORE_MODULE = "onboarding";

const CLIENT_TYPE_BY_HOLDER = {
  CORPORATE: "Company",
  INDIVIDUAL: "Individual",
};

export default {
  name: "OnboardingDetail",

  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
    AppTimeline,
    AppTimelineItem,
    OnboardingSummaryTab,
    OnboardingFormTab,
    OnboardingPackagesTab,
    OnboardingRequirementsTab,
    OnboardingDocumentsTab,
    FormPreviewModal: () => import("@/views/settings/admin-panel/onboarding-form-builder/FormPreviewModal.vue"),
  },

  data() {
    return {
      loading: true,
      account: null,
      onboarding: null,
      formData: null,
      formSchema: { sections: [] },
      signatures: null,
      summary: {},
      formSections: [],
      sectionCatalogue: [],
      personTypes: {},
      persons: [],
      appliedPackages: [],
      availablePackages: [],
      pendingPackageChanges: [],
      requirements: [],
      customerRequests: [],
      documents: [],
      documentTypes: [],
      contacts: [],
      activities: [],
      signatureImages: {},

      newContact: { name: "", email: "" },
      selectedClientUser: null,
      clientUsers: [],
      sendInviteEmail: true,
      contactSaving: false,

      contactFields: [
        { key: "Name", label: "Name" },
        { key: "Email", label: "Email" },
        { key: "Status", label: "Status" },
        { key: "Link", label: "Onboarding Link" },
        { key: "LastAccess", label: "Last Access" },
        { key: "actions", label: "" },
      ],
    };
  },

  computed: {
    accountChips() {
      const a = this.account || {};
      const holder = (a.HolderType || "").toString().toUpperCase().trim();
      return [
        { label: "Client type", value: CLIENT_TYPE_BY_HOLDER[holder] || a.HolderType || "—" },
        { label: "Account type", value: a.SettlementType ? String(a.SettlementType).toUpperCase() : "—" },
        { label: "Client category", value: a.ClientCategory || "—" },
      ];
    },
  },

  beforeCreate() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, onboardingStoreModule);
    }
  },

  mounted() {
    this.loadAll();
  },

  destroyed() {
    if (store.hasModule(STORE_MODULE)) {
      store.unregisterModule(STORE_MODULE);
    }
    Object.values(this.signatureImages).forEach((url) => URL.revokeObjectURL(url));
  },

  methods: {
    loadAll() {
      this.loading = true;
      Promise.all([
        store.dispatch(`${STORE_MODULE}/getAccountOnboarding`, { accountId: this.$route.params.id }),
        store.dispatch(`${STORE_MODULE}/getClientUsers`),
      ])
        .then(([onboardingRes, clientUsersRes]) => {
          this.applyPayload(onboardingRes.data);
          this.clientUsers = clientUsersRes.data || [];
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to load onboarding data.", { title: "Error", variant: "danger", solid: true });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    applyPayload(payload) {
      this.account = payload.account;
      this.onboarding = payload.onboarding;
      this.formData = payload.formData || null;
      this.formSchema = payload.formSchema && Array.isArray(payload.formSchema.sections) ? payload.formSchema : { sections: [] };
      this.signatures = payload.signatures || null;
      this.summary = payload.summary || {};
      this.formSections = payload.formSections || [];
      this.sectionCatalogue = payload.sectionCatalogue || [];
      this.personTypes = payload.personTypes || {};
      this.persons = payload.persons || [];
      this.appliedPackages = payload.appliedPackages || [];
      this.availablePackages = payload.availablePackages || [];
      this.pendingPackageChanges = payload.pendingPackageChanges || [];
      this.requirements = payload.requirements || [];
      this.customerRequests = payload.customerRequests || [];
      this.documents = payload.documents || [];
      this.documentTypes = payload.documentTypes || [];
      this.contacts = payload.contacts || [];
      this.activities = payload.activities || [];
      this.loadSignatures();
    },

    refresh() {
      return store
        .dispatch(`${STORE_MODULE}/getAccountOnboarding`, { accountId: this.$route.params.id })
        .then((res) => this.applyPayload(res.data))
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to reload onboarding data.", { title: "Error", variant: "danger", solid: true });
        });
    },

    // Signature files are stripped from the payload. The payload's `signatures` object says
    // which images exist (declaration, recategorisation, persons[], notices[]); without it
    // (legacy payload) we probe the classic ones once the form has been signed and ignore
    // the ones that do not exist.
    loadSignatures() {
      if (!this.onboarding) return;
      const available = this.signatures;
      const signed = !!this.onboarding.SignedDate;
      if (!available && !signed) return;

      const wanted = [];
      if (available ? available.declaration : signed) wanted.push("declaration");
      if (available ? available.recategorisation : signed) wanted.push("recategorisation");

      if (available) {
        (available.persons || []).forEach((personId) => wanted.push(`person:${personId}`));
        (available.notices || []).forEach((key) => wanted.push(`notice:${key}`));
      } else {
        const traderKey = (this.personTypes.Trader && this.personTypes.Trader.formKey) || "traders";
        const traders = (this.formData && this.formData.persons && this.formData.persons[traderKey]) || [];
        traders.forEach((trader, index) => {
          wanted.push(trader.personId ? `person:${trader.personId}` : `trader:${index}`);
        });
      }

      wanted.forEach((which) => {
        if (this.signatureImages[which]) return;
        store
          .dispatch(`${STORE_MODULE}/getSignatureImage`, { onboardingId: this.onboarding.id, which })
          .then((res) => {
            this.$set(this.signatureImages, which, URL.createObjectURL(res.data));
          })
          .catch(() => {});
      });
    },

    /* ---------- Form preview ---------- */
    openFormPreview() {
      if (!this.onboarding || !this.$refs.formPreview) return;
      this.$refs.formPreview.open({ onboardingId: this.onboarding.id });
    },

    /* ---------- Contacts ---------- */
    createContact() {
      this.$refs.contactValidation.validate().then((valid) => {
        if (!valid) return;
        this.contactSaving = true;
        store
          .dispatch(`${STORE_MODULE}/createContact`, {
            onboardingId: this.onboarding.id,
            userId: this.selectedClientUser ? this.selectedClientUser.id : null,
            name: this.selectedClientUser ? null : this.newContact.name,
            email: this.selectedClientUser ? null : this.newContact.email,
            sendEmail: this.sendInviteEmail,
          })
          .then((res) => {
            this.$bvToast.toast(
              (res.data && res.data.message) ||
                (this.sendInviteEmail ? "Contact added and onboarding link emailed." : "Contact added."),
              { title: "Success", variant: "success", solid: true }
            );
            this.newContact = { name: "", email: "" };
            this.selectedClientUser = null;
            this.$refs.contactValidation.reset();
            return this.refresh();
          })
          .catch((err) => {
            console.error(err);
            this.$bvToast.toast(
              (err.response && err.response.data && err.response.data.message) || "Failed to add contact.",
              { title: "Error", variant: "danger", solid: true }
            );
          })
          .finally(() => {
            this.contactSaving = false;
          });
      });
    },

    contactLink(item) {
      return onboardingLink(item.Token);
    },

    contactStatusVariant(status) {
      if (status === "Active") return "success";
      if (status === "Frozen") return "warning";
      return "secondary";
    },

    copyLink(item) {
      const link = this.contactLink(item);
      navigator.clipboard.writeText(link).then(() => {
        this.$bvToast.toast("Link copied to clipboard.", { title: "Copied", variant: "success", solid: true });
      });
    },

    resendLink(item) {
      store
        .dispatch(`${STORE_MODULE}/resendContactLink`, { contactId: item.id })
        .then(() => {
          this.$bvToast.toast(`Link re-sent to ${item.Email}.`, { title: "Success", variant: "success", solid: true });
          return this.refresh();
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to send link.", { title: "Error", variant: "danger", solid: true });
        });
    },

    freezeContact(item) {
      this.$bvModal
        .msgBoxConfirm(`Freeze access for ${item.Name}? Their link will stop working until it is re-activated.`, {
          title: "Freeze Access",
          okVariant: "warning",
          okTitle: "Freeze",
          centered: true,
        })
        .then((confirmed) => {
          if (!confirmed) return;
          store
            .dispatch(`${STORE_MODULE}/freezeContact`, { contactId: item.id })
            .then(() => this.refresh())
            .catch((err) => {
              console.error(err);
              this.$bvToast.toast(
                (err.response && err.response.data && err.response.data.message) || "Failed to freeze contact.",
                { title: "Error", variant: "danger", solid: true }
              );
            });
        });
    },

    reactivateContact(item) {
      store
        .dispatch(`${STORE_MODULE}/reactivateContact`, { contactId: item.id })
        .then(() => {
          this.$bvToast.toast(`Access re-activated for ${item.Name}. Their existing link and PIN work again.`, {
            title: "Success",
            variant: "success",
            solid: true,
          });
          return this.refresh();
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast(
            (err.response && err.response.data && err.response.data.message) || "Failed to re-activate contact.",
            { title: "Error", variant: "danger", solid: true }
          );
        });
    },

    /* ---------- Activity ---------- */
    loadActivities() {
      store
        .dispatch(`${STORE_MODULE}/getActivityLog`, { onboardingId: this.onboarding.id })
        .then((res) => {
          this.activities = res.data || [];
        })
        .catch((err) => console.error(err));
    },

    activitySubtitle(activity) {
      let subtitle = activity.Description || "";
      if (activity.Ip) subtitle += ` — IP: ${activity.Ip}`;
      return subtitle;
    },

    formatChangePath(path) {
      const sectionLabels = {
        sectionA: "Section A",
        sectionB: "Section B",
        sectionC: "Section C",
        sectionD: "Section D",
        sectionE: "Section E",
        sectionF: "Section F",
        sectionG: "Section G",
        sectionH: "Section H",
        persons: "Persons",
        traders: "Traders",
        signatories: "Non-dealing signatories",
        directors: "Directors",
        ubos: "Ultimate beneficial owners",
        officers: "Officers",
        subFunds: "Sub-funds",
      };
      const fieldLabels = {
        accountName: "Account Name", clientType: "Client Type", address: "Address",
        contractsNameAddress: "Contracts Name & Address", reportFrequency: "Report Frequency",
        reportForm: "Report Form", companyRegNumber: "Company Reg. Number",
        taxCountry: "Country Tax Domiciled", natureOfBusiness: "Nature of Business",
        bankersNameAddress: "Name & Address of Bankers", telHome: "Tel / Home",
        telOffice: "Tel / Office", mobile: "Mobile", fax: "Fax", email: "Email",
        sortCodeSwift: "Sort Code / SWIFT", bankAccountName: "Bank Account Name",
        bankAccountNumber: "Bank Account Number", officerName: "Officer Name",
        authorisedTraders: "Authorised Traders", nonDealingSignatories: "Non-Dealing Signatories",
        yearsExperience: "Years of Experience", dealingFrequency: "Dealing Frequency",
        typicalSize: "Typical Transaction Size", capacity: "Capacity",
        tradingBasis: "Trading Basis", otherExperience: "Other Experience",
        understandsRisks: "Understands Risks", acknowledged: "Acknowledged",
        classification: "Classification", criteria: "Criteria", additionalInfo: "Additional Info",
        name: "Name", signatureImage: "Signature",
      };
      const parts = path.split(".");
      const out = [];
      parts.forEach((part) => {
        if (sectionLabels[part]) { out.push(sectionLabels[part]); return; }
        if (!isNaN(part)) { out.push(`#${parseInt(part, 10) + 1}`); return; }
        out.push(fieldLabels[part] || part.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()));
      });
      return out.join(" › ");
    },

    eventTitle(event) {
      const titles = {
        LinkSent: "Link Sent",
        Accessed: "Page Accessed",
        FormSaved: "Form Saved",
        FormSigned: "Form Signed",
        DocumentUploaded: "Document Uploaded",
        DocumentDeleted: "Document Deleted",
        ContactAdded: "Contact Added",
        ContactRevoked: "Contact Revoked",
        ContactFrozen: "Contact Access Frozen",
        ContactReactivated: "Contact Access Re-activated",
        PinVerified: "PIN Verified",
        PinFailed: "Incorrect PIN",
        PinLocked: "Link Locked",
        RequiredDocAdded: "Required Document Added",
        RequiredDocRemoved: "Required Document Removed",
        DocumentsSynced: "Necessary Documents Synced",
        DocumentsRequested: "Documents Requested by Email",
        OnboardingCreated: "Onboarding Opened",
        PackageAdded: "Package Added",
        PackageRemoved: "Package Removed",
        PackagesSynced: "Package Settings Applied",
        RequirementAdded: "Document Requested",
        RequirementRemoved: "Document Request Removed",
        DocumentApproved: "Document Approved",
        DocumentRejected: "Document Rejected",
        DocumentHeld: "Document Put Under Review",
        DocumentReattached: "Document Re-attached",
        CustomerRequestAdded: "Client Requested a File",
        CustomerRequestFulfilled: "Client Request Fulfilled",
        CustomerRequestDeclined: "Client Request Declined",
        EmailFailed: "Email Failed",
        FormReSigned: "Form Re-signed",
      };
      return titles[event] || event;
    },

    eventVariant(event) {
      const variants = {
        FormSigned: "success",
        Accessed: "info",
        DocumentUploaded: "primary",
        DocumentDeleted: "danger",
        ContactRevoked: "danger",
        ContactFrozen: "warning",
        ContactReactivated: "success",
        PinFailed: "warning",
        PinLocked: "danger",
        LinkSent: "warning",
        OnboardingCreated: "info",
        PackageAdded: "primary",
        PackageRemoved: "danger",
        PackagesSynced: "info",
        RequirementAdded: "primary",
        RequirementRemoved: "danger",
        DocumentApproved: "success",
        DocumentRejected: "danger",
        DocumentHeld: "warning",
        DocumentReattached: "info",
        CustomerRequestAdded: "warning",
        CustomerRequestFulfilled: "success",
        CustomerRequestDeclined: "danger",
        EmailFailed: "danger",
        FormReSigned: "success",
      };
      return variants[event] || "secondary";
    },

    eventIcon(event) {
      const icons = {
        LinkSent: "MailIcon",
        Accessed: "EyeIcon",
        FormSaved: "SaveIcon",
        FormSigned: "PenToolIcon",
        DocumentUploaded: "UploadIcon",
        DocumentDeleted: "Trash2Icon",
        ContactAdded: "UserPlusIcon",
        ContactRevoked: "UserXIcon",
        ContactFrozen: "PauseCircleIcon",
        ContactReactivated: "PlayCircleIcon",
        PinVerified: "UnlockIcon",
        PinFailed: "LockIcon",
        PinLocked: "LockIcon",
        RequiredDocAdded: "FilePlusIcon",
        RequiredDocRemoved: "FileMinusIcon",
        DocumentsSynced: "RefreshCwIcon",
        DocumentsRequested: "MailIcon",
        OnboardingCreated: "FolderPlusIcon",
        PackageAdded: "PackageIcon",
        PackageRemoved: "PackageIcon",
        PackagesSynced: "RefreshCwIcon",
        RequirementAdded: "FilePlusIcon",
        RequirementRemoved: "FileMinusIcon",
        DocumentApproved: "CheckCircleIcon",
        DocumentRejected: "XCircleIcon",
        DocumentHeld: "PauseCircleIcon",
        DocumentReattached: "LinkIcon",
        CustomerRequestAdded: "MessageSquareIcon",
        CustomerRequestFulfilled: "CheckSquareIcon",
        CustomerRequestDeclined: "SlashIcon",
        EmailFailed: "AlertTriangleIcon",
        FormReSigned: "PenToolIcon",
      };
      return icons[event] || "ActivityIcon";
    },

    statusVariant(status) {
      switch (status) {
        case "Draft":
          return "info";
        case "InProgress":
          return "warning";
        case "Signed":
          return "primary";
        case "Reopened":
          return "warning";
        case "Complete":
          return "success";
        default:
          return "secondary";
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
